import React, {useContext, useEffect, useState} from 'react';
import {Box, Flex, Image, Link, Spinner} from '@chakra-ui/react';
import {
  Routes,
  Route,
  useNavigate,
  matchPath,
  useLocation,
  Navigate,
} from 'react-router';
import {useLogout} from '../../hooks/api/logout';
import _ from 'lodash';
import {track} from '../../analytics';
import {useAuth0} from '@auth0/auth0-react';
import {useCompleteOnboarding} from '../../hooks/api/customer';
import {useActiveMarketId} from '../../hooks/api/metadata';
import {MarketProvider} from '../Market/MarketProvider';
import {useOnboardingSteps} from './OnboardingSteps';
import {useMarket} from '../../hooks/api/metadata';
import {PageIndicator} from '../PageIndicator';

type Navigate = (dir: 'next' | 'prev') => void;
export type OnboardingContext = {
  onPageNavigate: Navigate;
  isFirstStep: boolean;
};

const OnboardingContext = React.createContext<OnboardingContext | undefined>(
  undefined
);

export function useOnboardingContext() {
  const onboardingContext = useContext(OnboardingContext);
  if (!onboardingContext) {
    throw new Error('could not find OnboardingContext');
  }

  return onboardingContext;
}

export const Onboarding = () => {
  const {user} = useAuth0();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(0);

  const completeOnboarding = useCompleteOnboarding();
  const marketId = useActiveMarketId();
  const market = useMarket(marketId);
  const onboardingSteps = useOnboardingSteps();

  useEffect(() => {
    setPage(
      onboardingSteps.findIndex((step) =>
        matchPath(location.pathname, `/onboarding/${step.route}`)
      )
    );
  }, [location.pathname, onboardingSteps]);
  const step = onboardingSteps[page];

  const onPageNavigate: Navigate = (dir) => {
    const nextPage = dir === 'next' ? page + 1 : page - 1;
    const nextStep = onboardingSteps.at(nextPage);
    if (!nextStep) {
      if (dir === 'next') {
        // Complete onboarding if there are no more steps
        completeOnboarding.mutate({});
      }
      return;
    }

    const {name, route} = nextStep;
    track('onboardingAdvanced', {
      user,
      properties: {name: name, path: route, version: 'v2'},
    });
    navigate(nextStep.route, {relative: 'route'});
  };

  if (completeOnboarding.isPending) {
    return (
      <Flex
        bgColor={'kgray.100'}
        h="100%"
        w="100%"
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Spinner />
      </Flex>
    );
  }

  const onboardingContext = {
    onPageNavigate,
    isFirstStep: page === 1,
  };

  return (
    <Flex bgColor={'kgray.100'} h="100%" w="100%" overflow={'auto'} p={4}>
      <Flex
        bgColor={'white'}
        borderRadius={'xl'}
        direction="column"
        position="relative"
        w={step.sidebar ? {base: '100%', xl: '70%'} : '100%'}
      >
        <Flex alignItems={'baseline'} justifyContent={'space-between'} p={6}>
          <PageIndicator page={page} totalPages={onboardingSteps.length} />
          <Link color="kgray.200" fontSize="xs" onClick={() => logout.mutate()}>
            Logout
          </Link>
        </Flex>

        {!market ? (
          <></>
        ) : (
          <MarketProvider market={market}>
            <OnboardingContext.Provider value={onboardingContext}>
              <Flex position={'relative'} grow={1} h="100%">
                <Routes>
                  {onboardingSteps.map(({route, element: Element}) => (
                    <Route key={route} path={route} element={<Element />} />
                  ))}
                  <Route
                    path="*"
                    element={<Navigate to="/" replace={true} />}
                  />
                </Routes>
              </Flex>
            </OnboardingContext.Provider>
          </MarketProvider>
        )}
      </Flex>

      {step.sidebar && (
        <Flex
          alignItems={'center'}
          direction="column"
          display={{base: 'none', xl: 'flex'}}
          justifyContent={'center'}
          mx={14}
          gap={4}
          w={'30%'}
        >
          <Image
            src={step.sidebar.image}
            w="100%"
            minW="355px"
            boxSize="500px"
          />
          <Box textAlign={'center'} color={'kgray.300'}>
            {step.sidebar.text}
          </Box>
        </Flex>
      )}
    </Flex>
  );
};
